/**
 * English — `pageTour` namespace: per-page guided tours ("Guide to this page",
 * the ? button in the header). Keys: pageTour.<tourId>.<step>.{tag,title,body}
 * (see composables/pageTours.ts).
 */
export default {
    pageTour: {
        button: {
            label: 'Guide to this page',
        },
        controls: {
            next: 'Next',
            back: 'Back',
            done: 'Done',
            stepOf: 'Step {current} of {total}',
        },

        dashboard: {
            aggregate: {
                tag: 'Overview',
                title: 'Group exposure',
                body: 'The combined net position of every locked (LOCKED) contract per 15-minute bucket, stacked by country (HR + SI). Above zero = SHORT (under-procured), below zero = LONG (surplus). The CRM dashboard draws its "futures ceiling" line from the same position snapshots.',
            },
            toolbar: {
                tag: 'Chart filters',
                title: 'Range, country and split',
                body: 'Pick FROM/TO (default: today + 6 days), narrow to HR or SI, and switch on the forecast/futures split. The chart axis only spans days that carry data, so it can be narrower than the range you picked — the yellow note tells you when that happens.',
            },
            refresh: {
                tag: 'Refresh',
                title: 'Pull fresh snapshots',
                body: 'The position merge runs in the background on every APPROVED ↔ LOCKED transition. After you lock a contract, click Refresh so the chart reads the freshly written snapshots.',
            },
            list: {
                tag: 'Contract list',
                title: 'Futures contracts',
                body: 'Each row is one futures contract: side (BUY/SELL), product type, country, status, counterparty, delivery period, volume and price. Clicking a row opens its summary on the right; Up/Down arrows and Enter work from the keyboard.',
            },
            filters: {
                tag: 'List filters',
                title: 'Side, status, country',
                body: 'The list is filtered to status APPROVED by default — drafts (DRAFT) and locked (LOCKED) contracts stay hidden until you switch the status to "All" or another value. Filters persist while you stay in the app.',
            },
            newContract: {
                tag: 'Primary action',
                title: 'New contract',
                body: 'Opens the new-contract form in the right pane. Visible only to operators with the futures:write permission.',
            },
            viewPosition: {
                tag: 'Position',
                title: 'View position',
                body: 'Opens that contract\'s position page: how much it contributes to the total position per day, against the forecast and the other futures contracts.',
            },
            form: {
                tag: 'Right pane',
                title: 'New contract form',
                body: 'When no contract is selected, you enter a new one here: counterparty, country, side, product, delivery period, MTU fill in MW and price. "Continue" creates the contract in DRAFT status and takes you to the bin editor.',
            },
            summary: {
                tag: 'Right pane',
                title: 'Contract summary',
                body: 'Overview of the selected contract with the status buttons (Validate → Approve → Lock). "Edit contract" opens the header, "New contract" brings the form back.',
            },
        },

        contractEditor: {
            header: {
                tag: 'Header',
                title: 'Contract and status',
                body: 'Counterparty, side (BUY/SELL), product type, profile source, status and version; the line below gives the delivery period, total volume and price. "Edit contract" changes the header (not the bins) — amending an approved contract automatically reverts it to VALIDATED.',
            },
            status: {
                tag: 'Status workflow',
                title: 'Validate, approve, lock',
                body: 'DRAFT → VALIDATED → APPROVED → LOCKED; cancelling is final. Approve, Lock and Unlock require the futures:approve permission. Only a locked (LOCKED) contract enters the position merge — and thereby the group exposure and the ceiling in the CRM.',
            },
            dayNav: {
                tag: 'Delivery days',
                title: 'Day navigation',
                body: 'Bins are edited one day at a time. The arrows or Alt+← / Alt+→ change the day; the line below says which day you are on and how many buckets it has — 96 normally, 92 on the spring-forward day, 100 on the fall-back day.',
            },
            grid: {
                tag: 'Bins',
                title: 'MW per 15-minute bucket',
                body: 'The four rows are quarters of the day (00–06, 06–12, 12–18, 18–24 local time); the 08–20 peak window is shaded. Each cell is power in MW for that bucket (MTU), not energy in MWh. Edits are kept immediately but only saved with "Confirm & save".',
            },
            toolbar: {
                tag: 'Day tools',
                title: 'Copy and reset day',
                body: 'Copy carries this day\'s profile onto other days you pick; Reset sets every bin of the day to zero. Both are disabled on a locked contract.',
            },
            liveChart: {
                tag: 'Chart',
                title: 'Live day profile',
                body: 'Draws the current day as you type — a quick check that PEAK is filled only 08–20 and BASE is flat across the whole day.',
            },
            footer: {
                tag: 'Saving',
                title: 'Totals and Confirm & save',
                body: 'Left: the sum of all bins, the number of days and how many are unsaved. "Confirm & save" (or Ctrl+S) opens the per-day review and submits a new version; "Discard" returns to the dashboard. A locked contract is read-only.',
            },
            audit: {
                tag: 'Audit',
                title: 'Change history',
                body: 'Every creation, amendment and status change with time, actor and a before/after diff. The tab also opens directly from a link ending in #audit.',
            },
        },

        contractEditHeader: {
            form: {
                tag: 'Contract header',
                title: 'Editing the header',
                body: 'This is where the contract data changes, not the bins — for the per-bucket profile go back to the editor. Saving creates a new version; if someone saved theirs in the meantime, the server rejects yours.',
            },
            counterparty: {
                tag: 'Identification',
                title: 'Counterparty and references',
                body: 'The counterparty name is required; the EIC and external deal id are optional and link the contract to the counterparty\'s documents and systems.',
            },
            country: {
                tag: 'Country',
                title: 'HR or SI',
                body: 'The country decides the market calendar (peak hours, holidays) and the position book the contract belongs to. After creation only an operator with futures:approve can change it — the change re-resolves the profile.',
            },
            sideProduct: {
                tag: 'Side and product',
                title: 'BUY/SELL and product type',
                body: 'BUY lowers the net position (procurement), SELL raises it. BASE fills every bucket, PEAK only 08–20, OFF_PEAK only outside that, and CUSTOM leaves the bins for manual entry in the editor.',
            },
            delivery: {
                tag: 'Delivery period',
                title: 'FROM / TO and quick picks',
                body: 'The chips (day, week, month, quarter, year) set the range around the start date. On a MANUAL_BILATERAL contract the range is locked because the bins are bound to that window — create a new contract for a different range.',
            },
            profileSource: {
                tag: 'Profile source',
                title: 'EPEX_DEFAULT or MANUAL_BILATERAL',
                body: 'EPEX_DEFAULT derives the bins from the product type and the MTU fill; MANUAL_BILATERAL expects bins set by hand in the editor.',
            },
            volume: {
                tag: 'Volume and price',
                title: 'MW, not MWh',
                body: 'The MTU fill is power in MW placed into every active 15-minute bucket; the total volume is derived as fill × number of active buckets. The price is in €/MW and the currency is always EUR.',
            },
            submit: {
                tag: 'Primary action',
                title: 'Save changes',
                body: 'Submits the header amendment and returns to the bin editor; Cancel goes back without saving. If the period overlaps an existing contract the server rejects the save and shows links to the conflicting contracts above.',
            },
        },

        position: {
            header: {
                tag: 'Header',
                title: 'One contract\'s position',
                body: 'This page is read-only: it shows how much this contract contributes to the total position per day. The line below says when the last merge ran and how many days and buckets it covers.',
            },
            noSnapshot: {
                tag: 'No snapshot',
                title: 'Merge has not run yet',
                body: 'Position snapshots only exist once the contract is locked (LOCKED) and the merge has run. Until then there is no chart.',
            },
            dayNav: {
                tag: 'Day picker',
                title: 'Day by day',
                body: 'The dropdown lists every day of the delivery period; the arrows or Alt+← / Alt+→ step to the neighbouring day.',
            },
            toggles: {
                tag: 'Layers',
                title: 'What to show',
                body: 'Switch the forecast baseline, this contract and the other futures contracts on or off. The LONG/SHORT badge is the direction by majority of the day\'s buckets.',
            },
            chart: {
                tag: 'Chart',
                title: 'Stacked position',
                body: 'Layers: forecast, this contract\'s contribution and the remainder of the other futures contracts; the line is the final position. A SELL contract adds, a BUY subtracts. If no forecast has been processed yet, the forecast layer is zero.',
            },
            summary: {
                tag: 'Day summary',
                title: 'Numbers for the selected day',
                body: 'The sum of the forecast, this contract\'s contribution and the final position for the day, plus the direction. The values are sums over the buckets.',
            },
            reference: {
                tag: 'Reference',
                title: 'Product shapes',
                body: 'Static illustrations of what BASE, PEAK and OFF_PEAK profiles look like and how they add up into a combined futures position. These are not this contract\'s data — the real cross-contract total is on the dashboard.',
            },
        },
    },
}
